import { ErrorBoundary } from '@/components/upseat-ui/app/ErrorBoundary'
import { useUserStore } from '@/context/useUserStore'
import { useTrackPopupRouteVisits } from '@/hooks/notifications/useTrackPopupRouteVisits'
import { buildPostLoginPath } from '@/libs/utils/postLoginRoute'
import { FnBConfirmation } from '@/pages/foodAndDrinks/confirmation'
import RedirectEngine from '@/services/Login/redirectEngine'
import { isVerifyInProgress } from '@/services/Login/verifyFlag'
import { Suspense, lazy, useMemo } from 'react'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from 'react-router-dom'

// Lazy load pages
const Wallet = lazy(() => import('@/pages/wallet'))
const Upbooking = lazy(() => import('@/pages/seatMap'))
const SwapDates = lazy(() => import('@/pages/swapDates'))
const Account = lazy(() => import('@/pages/account/Account'))
const Memberships = lazy(() => import('@/pages/memberships/Memberships'))
const Orders = lazy(() => import('@/pages/account/Orders'))
const HelpCentre = lazy(() => import('@/pages/account/HelpCentre'))
const Partners = lazy(() => import('@/pages/account/Partners'))
const BookingSummary = lazy(
  () => import('@/pages/bookingSummary/BookingSummary'),
)
const FnbBookingSummary = lazy(
  () => import('@/pages/fnbBookingSummary/FnbBookingSummary'),
)
const Send = lazy(() => import('@/pages/shareTickets'))
const FoodAndDrinks = lazy(() => import('@/pages/foodAndDrinks'))
const LegalsCountries = lazy(() => import('@/pages/legals/LegalsCountries'))
const LegalsIndex = lazy(() => import('@/pages/legals/Legals'))
const TermsOfUse = lazy(() => import('@/pages/legals/TermsOfUse'))
const ContentChannel = lazy(() => import('@/pages/contentChannel'))
const CookiePolicy = lazy(() => import('@/pages/legals/CookiePolicy'))
const PrivacyPolicy = lazy(() => import('@/pages/legals/PrivacyPolicy'))
const NotificationsList = lazy(() => import('@/pages/notifications'))
const ExtraDetails = lazy(() => import('@/pages/account/ExtraDetails'))
// Also mounted here, not just in the public tree: a stale session must still be
// able to redeem an inbound token. See AuthVerifyRedirect.
const VerifyToken = lazy(() => import('@/pages/account/VerifyToken'))
const SeatMapPreview = lazy(() => import('@/pages/seatMapPreview'))
const CookiesPreferences = lazy(() =>
  import('@/components/upseat-ui/login/CookiesPreferences').then((m) => ({
    default: m.CookiesPreferences,
  })),
)

// Routes that must resolve before consent can be asked: they either work out
// where the user is going or collect details that come first.
const COOKIE_GATE_EXEMPT = ['/auth/verify', '/u/', '/legals']

const AuthVerifyRedirect = () => {
  const [searchParams] = useSearchParams()

  if (isVerifyInProgress()) return null

  // An inbound token always wins over the live session: it may belong to a
  // different account, and skipping it left the previous user signed in while
  // routing them to the new user's destination.
  if (searchParams.get('token')) return <VerifyToken />

  const to = buildPostLoginPath(
    searchParams.get('section') ?? searchParams.get('redirect'),
    {
      orderUid: searchParams.get('orderUid'),
      instanceUid: searchParams.get('instanceUid'),
      channelUid: searchParams.get('channelUid'),
      contentUid: searchParams.get('contentUid'),
    },
  )

  return <Navigate to={to} replace />
}

const ProtectedRoutes = () => {
  const [searchParams] = useSearchParams()
  const { pathname, search } = useLocation()
  const { user } = useUserStore()
  useTrackPopupRouteVisits()
  const previewMode =
    useMemo(() => searchParams.get('preview'), [searchParams]) === 'true' ||
    localStorage.getItem('iframe_mode') === 'true'
  const channelUid = localStorage.getItem('channel_uid')
  const rawCategories = localStorage.getItem('categories')
  const categories =
    rawCategories && rawCategories !== 'null' && rawCategories !== 'undefined'
      ? rawCategories
      : null
  const sref = localStorage.getItem('sref')

  if (
    user?.preferences_required &&
    !COOKIE_GATE_EXEMPT.some((path) => pathname.startsWith(path))
  ) {
    return (
      <Suspense fallback={<div className="w-full h-[100dvh] bg-purple" />}>
        <CookiesPreferences redirectTo={`${pathname}${search}`} />
      </Suspense>
    )
  }

  return (
    <Suspense fallback={<div className="w-full h-[100dvh] bg-purple" />}>
      <ErrorBoundary>
        <Routes>
          <Route path="/" element={<Navigate to="/tickets" replace />} />
          <Route path="/auth/verify" element={<AuthVerifyRedirect />} />
          <Route path="/u/:shortcode" element={<RedirectEngine />} />
          <Route path="/extra-details" element={<ExtraDetails />} />
          <Route
            path="/tickets"
            element={
              <ErrorBoundary>
                <Wallet />
              </ErrorBoundary>
            }
          />
          <Route
            path="/seat-map"
            element={
              <ErrorBoundary>
                <Upbooking />
              </ErrorBoundary>
            }
          />
          <Route
            path="/swap-dates"
            element={
              <ErrorBoundary>
                <SwapDates />
              </ErrorBoundary>
            }
          />
          <Route
            path="/memberships"
            element={
              <ErrorBoundary>
                <Memberships />
              </ErrorBoundary>
            }
          />
          <Route
            path="/account"
            element={
              <ErrorBoundary>
                <Account />
              </ErrorBoundary>
            }
          />
          <Route
            path="/share-tickets/:id"
            element={
              <ErrorBoundary>
                <Send />
              </ErrorBoundary>
            }
          />
          <Route
            path="/orders"
            element={
              <ErrorBoundary>
                <Orders />
              </ErrorBoundary>
            }
          />
          <Route
            path="/orders/:id?"
            element={
              <ErrorBoundary>
                <Orders />
              </ErrorBoundary>
            }
          />
          <Route
            path="/help-centre"
            element={
              <ErrorBoundary>
                <HelpCentre />
              </ErrorBoundary>
            }
          />
          <Route
            path="/partners"
            element={
              <ErrorBoundary>
                <Partners />
              </ErrorBoundary>
            }
          />
          <Route
            path="/booking-summary"
            element={
              <ErrorBoundary>
                <BookingSummary />
              </ErrorBoundary>
            }
          />
          <Route
            path="/fnb-booking-summary"
            element={
              <ErrorBoundary>
                <FnbBookingSummary />
              </ErrorBoundary>
            }
          />
          <Route
            path="/channel/:id"
            element={
              <ErrorBoundary>
                <ContentChannel />
              </ErrorBoundary>
            }
          />
          <Route
            path="/food-and-drinks"
            element={
              <ErrorBoundary>
                <FoodAndDrinks />
              </ErrorBoundary>
            }
          />
          <Route
            path="/preview"
            element={
              <Navigate
                to={`/channel/${channelUid}?preview=true${categories ? `&categories=${categories}` : ''}${sref ? `&sref=${sref}` : ''}`}
                replace
              />
            }
          />

          {process.env.NODE_ENV === 'development' && (
            <Route
              path="/test/lyceum"
              element={
                <ErrorBoundary>
                  <SeatMapPreview />
                </ErrorBoundary>
              }
            />
          )}

          <Route
            path="/food-and-drinks/confirmation/:id"
            element={
              <ErrorBoundary>
                <FnBConfirmation />
              </ErrorBoundary>
            }
          />
          <Route
            path="/notifications"
            element={
              <ErrorBoundary>
                <NotificationsList />
              </ErrorBoundary>
            }
          />
          {/* Deep link (push/email): renders the list, which opens the detail
              drawer and puts the URL back to /notifications. */}
          <Route
            path="/notification/:uid"
            element={
              <ErrorBoundary>
                <NotificationsList />
              </ErrorBoundary>
            }
          />
          <Route
            path="/legals"
            element={
              <ErrorBoundary>
                <LegalsCountries />
              </ErrorBoundary>
            }
          />
          <Route
            path="/legals/terms"
            element={<Navigate to="/legals/uk/terms" replace />}
          />
          <Route
            path="/legals/privacy"
            element={<Navigate to="/legals/uk/privacy" replace />}
          />
          <Route
            path="/legals/cookies"
            element={<Navigate to="/legals/uk/cookies" replace />}
          />
          <Route
            path="/legals/:country"
            element={
              <ErrorBoundary>
                <LegalsIndex />
              </ErrorBoundary>
            }
          />
          <Route
            path="/legals/:country/terms"
            element={
              <ErrorBoundary>
                <TermsOfUse />
              </ErrorBoundary>
            }
          />
          <Route
            path="/legals/:country/privacy"
            element={
              <ErrorBoundary>
                <PrivacyPolicy />
              </ErrorBoundary>
            }
          />
          <Route
            path="/legals/:country/cookies"
            element={
              <ErrorBoundary>
                <CookiePolicy />
              </ErrorBoundary>
            }
          />

          <Route
            path="*"
            element={
              previewMode ? (
                <Navigate
                  to={`/channel/${channelUid}?preview=true${
                    categories ? `&categories=${categories}` : ''
                  }${sref ? `&sref=${sref}` : ''}`}
                />
              ) : (
                <Navigate to="/tickets" replace />
              )
            }
          />
        </Routes>
      </ErrorBoundary>
    </Suspense>
  )
}

export default ProtectedRoutes
